import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import NotFoundPage from './NotFoundPage';
import Seo from '../components/Seo';
import { resolveRedirect } from '../lib/redirects';
import { hashString } from '../components/CustomCss';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const PAGE_CSS_LINK_ID = 'explora-cms-page-css';

const CmsOrNotFound = () => {
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasStoredSeo, setHasStoredSeo] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const alias = location.pathname.replace(/^\//, '');

        // Check for a redirect / "gone" entry first.
        const outcome = await resolveRedirect(location.pathname);
        if (outcome === 'redirected') return;      // navigating away
        if (outcome === 'gone') {                   // 410/404 — show NotFound
          setNotFound(true);
          setLoading(false);
          return;
        }

        const pageRes = await axios.get(`${API_BASE}/pages/${encodeURIComponent(alias)}`);

        // Resolve stored-SEO presence BEFORE rendering, so page + loading + seo
        // state updates all batch into ONE commit. Otherwise an await between
        // setPage() and setLoading(false) renders the spinner first (content not
        // mounted) — the [page] effects (custom_js + <script> revival, e.g. the
        // gallery lightbox) would then fire with no DOM to bind to.
        let stored = false;
        try {
          const seoRes = await axios.get(`${API_BASE}/page-seo/resolve?path=${encodeURIComponent(location.pathname)}`);
          stored = !!(seoRes.data && seoRes.data.seo);
        } catch (e) { stored = false; }

        setHasStoredSeo(stored);
        setPage(pageRes.data);
        setLoading(false);
      } catch (err) {
        setNotFound(true);
        setLoading(false);
      }
    };
    fetchPage();
  }, [location.pathname]);

  // Execute the page's custom JavaScript (if any) AFTER the HTML has been rendered.
  // Re-runs whenever the page payload changes. Cleans up on unmount / route change
  // so scripts don't leak between pages.
  useEffect(() => {
    if (!page || !page.custom_js) return;
    const scriptEl = document.createElement('script');
    scriptEl.type = 'text/javascript';
    // Wrap in an IIFE so the user's code doesn't pollute the global scope.
    scriptEl.text = `(function(){\ntry {\n${page.custom_js}\n} catch (e) { console.error('Custom page JS error:', e); }\n})();`;
    scriptEl.setAttribute('data-cms-page-js', page.alias || '');
    document.body.appendChild(scriptEl);
    return () => {
      if (scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [page]);

  // React's dangerouslySetInnerHTML does NOT execute <script> tags. Revive them
  // so authors can include <script src="..."> (external) or inline <script>...</script>
  // directly inside the HTML Content of a CMS page.
  useEffect(() => {
    if (!page || !contentRef.current) return;
    const container = contentRef.current;
    const oldScripts = Array.from(container.querySelectorAll('script'));
    const newScripts = [];
    oldScripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.text) newScript.text = oldScript.text;
      oldScript.parentNode.replaceChild(newScript, oldScript);
      newScripts.push(newScript);
    });
    return () => {
      // Clean up scripts we added when the page changes/unmounts
      newScripts.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, [page]);

  // Inject the page's per-page custom CSS as an EXTERNAL stylesheet (no inline
  // <style>). Global + .cms-page-content base rules come from the app-wide
  // /api/custom-css.css stylesheet. `?v=` busts the cache when the CSS changes.
  useEffect(() => {
    if (!page) return undefined;
    const hasCss = (page.custom_css || '').trim() || (page.custom_css_mobile || '').trim();
    if (!hasCss) return undefined;
    const version = hashString(`${page.custom_css || ''}|${page.custom_css_mobile || ''}`);
    const href = `${API_BASE}/custom-css.css?page=${encodeURIComponent(page.alias)}&v=${version}`;
    let link = document.getElementById(PAGE_CSS_LINK_ID);
    if (!link) {
      link = document.createElement('link');
      link.id = PAGE_CSS_LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    return () => {
      const el = document.getElementById(PAGE_CSS_LINK_ID);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [page]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (notFound || !page) {
    return <NotFoundPage />;
  }

  const R2_BASE = 'https://media.explora.mu';
  const resolveR2 = (html) => html.replace(/\/r2\//g, `${R2_BASE}/`);

  return (
    <div className="custom-pages px-4 py-12" data-testid="cms-page">
      <Seo
        title={page.meta_title || page.title}
        description={page.meta_description}
        canonical={`${typeof window !== 'undefined' ? window.location.origin : ''}/${page.alias}`}
      />
      <div
        ref={contentRef}
        className="cms-page-content prose prose-gray"
        dangerouslySetInnerHTML={{ __html: resolveR2(page.html_content) }}
      />
      {/* Structured data (JSON-LD) — only the legacy raw injection when there is
          no page_seo record yet. Once migrated, <Seo> renders JSON-LD from page_seo. */}
      {!hasStoredSeo && page.structured_data && (
        <div dangerouslySetInnerHTML={{ __html: page.structured_data }} />
      )}
    </div>
  );
};

export default CmsOrNotFound;
